import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const title = "Gestion de projets";
const heroImage = "https://net-basis.net/Bilder/project-management-planning-control-dach-market-entry.webp";
const heroAlt = "Un homme d'affaires crée un schéma de processus pour gérer un projet d'entrée sur le marché.";
const heroClaimLine1 = "Parfaitement planifié !";
const heroClaimLine2 = "Géré en toute sécurité !";
const widget2 = "Votre partenaire expérimenté pour un conseil complet en gestion de projet et un management de projet direct dans la région DACH. Nous pilotons les projets complexes d'entrée sur le marché des constructeurs étrangers de manière ciblée, consciente des risques et avec une profonde expertise locale.";
const benefitsTitle = "Comment vous profitez de notre expertise en __gestion de projets__ :";
const p1 = "NetBasis International soutient ses partenaires constructeurs lors de leur entrée sur le marché, sur demande, par un conseil approfondi en gestion de projet et un management de projet direct. En effet, le projet de vouloir être présent sur de nouveaux marchés est, dans la plupart des cas, une décision stratégique et donc très importante. Et un échec doit absolument être évité. En même temps, une telle entreprise est souvent extrêmement complexe et exige un niveau d'expertise sectorielle, de compétence professionnelle et d'orientation ciblée plus élevé que celui dont dispose généralement une entreprise qui n'est pas présente sur place.";
const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

const benefits = [
  "Prise en charge fiable de la planification, du suivi et du pilotage d'un projet d'entrée sur le marché",
  "Aide structurée pour l'harmonisation du périmètre des prestations, des échéances du projet, des coûts, du financement et des ressources nécessaires",
  "Réalisation rigoureuse de l'intégralité du contrôle de projet local sur le terrain",
  "Soutien approfondi pour l'identification, l'analyse, l'évaluation et la maîtrise des risques liés au projet",
  "Observation continue de l'état d'avancement du projet et comparaison précise avec la planification initiale",
  "Suivi permanent du respect des délais, de la progression des contenus et de la maîtrise des coûts",
  "Information continue sur le projet et communication transparente avec le partenaire constructeur",
  "Assistance compétente pour toutes les affaires juridiques spécifiques au pays et au marché",
];

export default function GestionDeProjetsFrPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Gestion de Projets | NetBasis International"
        description="Pilotage efficace et mise en oeuvre opérationnelle de vos projets d’implantation et de vente par des experts sectoriels expérimentés."
        ogUrl="https://net-basis.net/fr/services/developpement-commercial/gestion-de-projets"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex items-start justify-start pt-[22%] md:pt-[10%] lg:pt-[12%] pl-[8%] md:pl-[10%] pointer-events-none">
                <p
                  className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-center leading-[1.15] md:leading-[1.5] animate-[trainingTextSlide_12s_ease-in-out_infinite]"
                  style={{
                    fontFamily: '"Open Sans", sans-serif',
                  }}
                >
                  <span className="block">{heroClaimLine1}</span>
                  {heroClaimLine2 && <span className="block">{heroClaimLine2}</span>}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              <h1 className="text-sm md:text-base font-semibold text-foreground-950 mb-4">{widget2}</h1>
              <p>
                {(() => {
                  const parts = p1.split(COMPANY_NAME);
                  return parts.map((part, i) => (
                    <span key={i}>
                      {part}
                      {i < parts.length - 1 && (
                        <strong className="text-primary-600">{COMPANY_NAME}</strong>
                      )}
                    </span>
                  ));
                })()}
              </p>
            </div>

            {/* Green heading with italic word */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i}>{part}</em> : part
              )}
            </h2>

            {/* Benefits with green checkmarks */}
            <div className="space-y-1.5 md:space-y-2 text-foreground-950 leading-relaxed text-sm md:text-base">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 md:gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-600 text-base md:text-lg"></i>
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}